import gql from 'graphql-tag';
import * as React from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Card, CardBody } from '../../../components/Bootstrap';
import { AuthContext } from '../../../AuthService';
import { ErrorAlert } from '../../../components';
import { CreateAccountForm } from '../../../components/CreateAccountForm';
import { LoginForm } from '../../../components/LoginForm';
import { AcceptInvite, AcceptInviteVariables } from './__generated__/AcceptInvite';
import { Invite, InviteVariables } from './__generated__/Invite';
import './Invite.scss';
import { useLogin } from '../../../hooks/useLogin';

const INVITE_QUERY = gql`
  query Invite($id: ID!) {
    invite(id: $id) {
      firstName
      lastName
      email
      userId
    }
  }
`;

const ACCEPT_INVITE = gql`
  mutation AcceptInvite($id: ID!) {
    acceptInvite(inviteId: $id)
  }
`;

type UserInviteContentProps = {
  loading: boolean;
  error: Error | null;
  data?: Invite;
  onLogin: ({ email, password }) => Promise<any>;
};

const UserInviteContent: React.FC<UserInviteContentProps> = ({ onLogin, loading, data, error }) => {
  const auth = React.useContext(AuthContext);

  if (loading || !data) {
    return (
      <>
        <ErrorAlert error={error} />
        Loading...
      </>
    );
  }

  const invite = {
    firstName: data.invite.firstName,
    lastName: data.invite.lastName,
    email: data.invite.email,
  };

  if (data.invite.userId) {
    return <LoginForm auth={auth} email={invite.email} onLogin={onLogin} />;
  }

  return <CreateAccountForm auth={auth} invite={invite} onLogin={onLogin} buttonText='Register' />;
};

type RouterProps = RouteComponentProps<{ id: string }>;

export const UserInvite: React.FC<RouterProps> = ({ match, history }) => {
  const inviteId = match.params.id;
  const auth = React.useContext(AuthContext);
  const { loading, data, error } = useQuery<Invite, InviteVariables>(INVITE_QUERY, {
    variables: {
      id: inviteId,
    },
  });

  const [login] = useLogin();
  const [acceptInvite] = useMutation<AcceptInvite, AcceptInviteVariables>(ACCEPT_INVITE);

  const onLogin = React.useCallback(
    async ({ email, password }) => {
      const { accessToken } = await auth.login(email, password);
      await login(accessToken);
      await acceptInvite({
        variables: {
          id: inviteId,
        },
      });

      const { accessToken: updatedAccessToken } = await auth.login(email, password);
      await login(updatedAccessToken);

      history.push('/home');
    },
    [acceptInvite, history, auth, inviteId, login]
  );

  return (
    <div className='centered-block onboarding-block'>
      <Card className='p-4'>
        <CardBody>
          <UserInviteContent loading={loading} error={error ?? null} data={data} onLogin={onLogin} />
        </CardBody>
      </Card>
    </div>
  );
};
